import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { ToastViewport } from "./ToastViewport";

export type ToastVariant = "error" | "success" | "info";

export type ToastItem = {
  id: string;
  message: string;
  variant: ToastVariant;
};

export type ToastOptions = {
  variant?: ToastVariant;
  durationMs?: number;
};

export type ToastContextValue = {
  toast: (message: string, options?: ToastOptions) => void;
  error: (message: string) => void;
  success: (message: string) => void;
};

const DEFAULT_TOAST_DURATION_MS = 5000;

export const ToastContext = createContext<ToastContextValue | null>(null);

type ToastProviderProps = {
  children: ReactNode;
};

export function ToastProvider({ children }: ToastProviderProps): ReactElement {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, options?: ToastOptions) => {
      const trimmed = message.trim();
      if (!trimmed) {
        return;
      }

      const id = crypto.randomUUID();
      const variant = options?.variant ?? "info";
      const durationMs = options?.durationMs ?? DEFAULT_TOAST_DURATION_MS;

      setToasts((prev) => [...prev, { id, message: trimmed, variant }]);
      window.setTimeout(() => dismiss(id), durationMs);
    },
    [dismiss],
  );

  const showError = useCallback(
    (message: string) => toast(message, { variant: "error" }),
    [toast],
  );

  const showSuccess = useCallback(
    (message: string) => toast(message, { variant: "success" }),
    [toast],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      error: showError,
      success: showSuccess,
    }),
    [toast, showError, showSuccess],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}
